import React, { useState, useRef } from 'react';

const BASEURL = process.env.REACT_APP_API_URL;

const VariantsTable = ({ generatedVariants, setGeneratedVariants }) => {
  const [expandedRow, setExpandedRow] = useState(null);
  const fileInputRefs = useRef({});
  // Add these new states
  const [barcodeScanning, setBarcodeScanning] = useState({});
  const [manualBarcodeModal, setManualBarcodeModal] = useState({
    isOpen: false,
    variantIndex: null,
    inputValue: '',
    error: ''
  });
  const [originalBarcodes, setOriginalBarcodes] = useState({});

  const handleSelectAll = (e) => {
    const newVariants = [...generatedVariants];
    newVariants.forEach(v => v.selected = e.target.checked);
    setGeneratedVariants(newVariants);
  };

  const handleVariantSelection = (index, checked) => {
    const newVariants = [...generatedVariants];
    newVariants[index].selected = checked;
    setGeneratedVariants(newVariants);
  };

  const handleSkuChange = (index, value) => {
    const newVariants = [...generatedVariants];
    newVariants[index].sku = value;
    setGeneratedVariants(newVariants);
  };

  const handleAddImage = (index, file) => {
    if (file) {
      // Prevent default scroll behavior
      const scrollPosition = window.pageYOffset;

      const reader = new FileReader();
      reader.onload = (event) => {
        const newVariants = [...generatedVariants];
        if (!newVariants[index].imageUrls) newVariants[index].imageUrls = [];
        if (!newVariants[index].imageFiles) newVariants[index].imageFiles = [];
        newVariants[index].imageUrls.push(event.target.result);
        newVariants[index].imageFiles.push(file);
        setGeneratedVariants(newVariants);

        // Restore scroll position
        setTimeout(() => {
          window.scrollTo(0, scrollPosition);
        }, 0);
      };
      reader.readAsDataURL(file);
    }
  };

  // Trigger file input click without affecting scroll
  const triggerFileInput = (index) => {
    if (fileInputRefs.current[index]) {
      // Save current scroll position
      const scrollPosition = window.pageYOffset;

      // Click the input
      fileInputRefs.current[index].click();

      // Restore scroll position after a short delay
      setTimeout(() => {
        window.scrollTo(0, scrollPosition);
      }, 0);
    }
  };

  const handleRemoveImage = (variantIndex, imageIndex) => {
    const newVariants = [...generatedVariants];
    newVariants[variantIndex].imageUrls = (newVariants[variantIndex].imageUrls || [])
      .filter((_, i) => i !== imageIndex);
    newVariants[variantIndex].imageFiles = (newVariants[variantIndex].imageFiles || [])
      .filter((_, i) => i !== imageIndex);
    setGeneratedVariants(newVariants);
  };

  const handleStockChange = (index, value) => {
    const newVariants = [...generatedVariants];
    newVariants[index].stock = parseInt(value, 10) || 0;
    setGeneratedVariants(newVariants);
  };

  const handleStockIncrement = (index, amount) => {
    const newVariants = [...generatedVariants];
    const currentStock = parseInt(newVariants[index].stock, 10) || 0;
    const newStock = currentStock + amount;
    if (newStock >= 0) {
      newVariants[index].stock = newStock;
      setGeneratedVariants(newVariants);
    }
  };

  const handleDeleteVariant = (index) => {
    setGeneratedVariants(generatedVariants.filter((_, i) => i !== index));
    if (expandedRow === index) {
      setExpandedRow(null);
    } else if (expandedRow > index) {
      setExpandedRow(expandedRow - 1);
    }
  };

  const toggleRow = (index) => {
    setExpandedRow(expandedRow === index ? null : index);
  };

  // Add these functions after existing handlers
  const handleBarcodeInputChange = (e, index) => {
    const newBarcode = e.target.value;
    const newVariants = [...generatedVariants];
    newVariants[index].barCode = newBarcode;
    setGeneratedVariants(newVariants);
  };

  const handleBarcodeFocus = (index, currentBarcode) => {
    setOriginalBarcodes(prev => ({ ...prev, [index]: currentBarcode }));
  };

  const handleBarcodeBlur = (variant, index, newBarcode) => {
    const originalBarcode = originalBarcodes[index];

    // For new products, we don't need API calls, just local state
    if (originalBarcode !== newBarcode) {
      console.log('Barcode changed:', { index, originalBarcode, newBarcode });
    }

    // Clean up the stored original value
    setOriginalBarcodes(prev => {
      const newState = { ...prev };
      delete newState[index];
      return newState;
    });
  };

  const openManualBarcodeModal = (index, reason = 'manual') => {
    setManualBarcodeModal({
      isOpen: true,
      variantIndex: index,
      inputValue: generatedVariants[index]?.barCode || '',
      error: '',
      reason
    });
  };

  const closeManualBarcodeModal = () => {
    setManualBarcodeModal({
      isOpen: false,
      variantIndex: null,
      inputValue: '',
      error: ''
    });
  };

  const handleManualBarcodeSubmit = () => {
    const { variantIndex, inputValue } = manualBarcodeModal;

    if (!inputValue.trim()) {
      setManualBarcodeModal(prev => ({
        ...prev,
        error: 'Veuillez saisir un code-barres valide'
      }));
      return;
    }

    const newVariants = [...generatedVariants];
    newVariants[variantIndex].barCode = inputValue.trim();
    setGeneratedVariants(newVariants);

    closeManualBarcodeModal();
  };

  // Generate a unique barcode via backend
  const generateUniqueBarcode = async (index) => {
    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch(`${BASEURL}/Product/GenerateBarcode`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!response.ok) throw new Error('Failed');
      const data = await response.json();
      const code = data.codeABarre || data.CodeABarre;
      if (!code) throw new Error('Empty');
      const newVariants = [...generatedVariants];
      newVariants[index].barCode = code;
      setGeneratedVariants(newVariants);
    } catch (e) {
      console.error('Generate barcode error', e);
      alert('Erreur lors de la génération du code-barres');
    }
  };

  const startBarcodeScanning = async (index) => {
    setBarcodeScanning(prev => ({ ...prev, [index]: true }));
    
    try {
      // Check if we're in a secure context
      if (!window.isSecureContext) {
        throw new Error('Camera access requires HTTPS or localhost');
      }
      
      // Check if camera is available
      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        throw new Error('Camera not supported');
      }

      // Request camera access with mobile-optimized settings
      const stream = await navigator.mediaDevices.getUserMedia({ 
        video: { 
          facingMode: { ideal: 'environment' },
          width: { ideal: 1280 },
          height: { ideal: 720 }
        } 
      });
      
      const modalHtml = `
        <div id="barcode-scanner-modal" class="fixed inset-0 z-50 overflow-y-auto">
          <div class="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div class="fixed inset-0 bg-gray-900 bg-opacity-90 transition-opacity"></div>
            <div class="inline-block align-bottom bg-white rounded-xl text-left overflow-hidden shadow-2xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
              <div class="bg-white px-6 pt-6 pb-4">
                <div class="flex items-start">
                  <div class="flex-shrink-0">
                    <div class="flex items-center justify-center h-12 w-12 rounded-full bg-blue-100">
                      <svg class="h-6 w-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z"></path>
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 13a3 3 0 11-6 0 3 3 0 016 0z"></path>
                      </svg>
                    </div>
                  </div>
                  <div class="ml-4 w-full">
                    <h3 class="text-lg leading-6 font-semibold text-gray-900 mb-4">Scanner le code-barres</h3>
                    <div class="relative">
                      <video id="barcode-video" class="w-full h-64 bg-black rounded-lg border-2 border-gray-300" autoplay playsinline></video>
                      <canvas id="barcode-canvas" class="hidden"></canvas>
                      
                      <!-- Scanning overlay -->
                      <div class="absolute inset-0 pointer-events-none">
                        <div class="absolute inset-4 border-2 border-dashed border-white opacity-50 rounded-lg"></div>
                        <div class="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2">
                          <div class="w-48 h-32 border-2 border-red-500 rounded-lg relative">
                            <div class="absolute top-0 left-0 w-6 h-6 border-t-4 border-l-4 border-red-500"></div>
                            <div class="absolute top-0 right-0 w-6 h-6 border-t-4 border-r-4 border-red-500"></div>
                            <div class="absolute bottom-0 left-0 w-6 h-6 border-b-4 border-l-4 border-red-500"></div>
                            <div class="absolute bottom-0 right-0 w-6 h-6 border-b-4 border-r-4 border-red-500"></div>
                          </div>
                        </div>
                      </div>
                      
                      <div class="absolute bottom-2 left-2 right-2">
                        <div class="bg-black bg-opacity-70 text-white text-xs px-3 py-2 rounded text-center">
                          <div id="scan-status">Pointez la caméra vers le code-barres</div>
                          <div class="mt-1 text-yellow-300">Assurez-vous que le code-barres est bien éclairé et net</div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div class="bg-gray-50 px-6 py-4 flex flex-col sm:flex-row sm:justify-end space-y-2 sm:space-y-0 sm:space-x-3">
                <button id="capture-btn" type="button" class="w-full sm:w-auto inline-flex justify-center rounded-lg border border-blue-300 shadow-sm px-4 py-2 bg-blue-50 text-sm font-medium text-blue-700 hover:bg-blue-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500">
                  📸 Capturer
                </button>
                <button id="manual-entry-btn" type="button" class="w-full sm:w-auto inline-flex justify-center rounded-lg border border-gray-300 shadow-sm px-4 py-2 bg-white text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500">
                  ✏️ Saisie manuelle
                </button>
                <button id="close-scanner" type="button" class="w-full sm:w-auto inline-flex justify-center rounded-lg border border-transparent shadow-sm px-4 py-2 bg-red-600 text-sm font-medium text-white hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500">
                  ❌ Fermer
                </button>
              </div>
            </div>
          </div>
        </div>
      `;
      
      document.body.insertAdjacentHTML('beforeend', modalHtml);
      const modal = document.getElementById('barcode-scanner-modal');
      const videoElement = document.getElementById('barcode-video');
      const canvasElement = document.getElementById('barcode-canvas');
      const closeButton = document.getElementById('close-scanner');
      const manualEntryButton = document.getElementById('manual-entry-btn');
      const captureButton = document.getElementById('capture-btn');
      const scanStatus = document.getElementById('scan-status');
      
      videoElement.srcObject = stream;
      videoElement.play();

      let scanInterval = null;
      let isScanning = true;

      // Setup canvas for image processing
      const ctx = canvasElement.getContext('2d');

      // Try multiple scanning approaches
      const startScanning = () => {
        scanInterval = setInterval(async () => {
          if (!isScanning) return;

          try {
            // Method 1: Try BarcodeDetector API (if available)
            if ('BarcodeDetector' in window) {
              try {
                const barcodeDetector = new window.BarcodeDetector();
                const barcodes = await barcodeDetector.detect(videoElement);
                if (barcodes.length > 0) {
                  const barcode = barcodes[0].rawValue;
                  onBarcodeDetected(barcode);
                  return;
                }
              } catch (e) {
                console.log('BarcodeDetector failed, trying alternative methods');
              }
            }

            // Method 2: Canvas-based analysis (simplified pattern detection)
            if (videoElement.videoWidth > 0 && videoElement.videoHeight > 0) {
              canvasElement.width = videoElement.videoWidth;
              canvasElement.height = videoElement.videoHeight;
              ctx.drawImage(videoElement, 0, 0);
              
              // Simple barcode pattern detection (basic implementation)
              const imageData = ctx.getImageData(0, 0, canvasElement.width, canvasElement.height);
              const detected = detectBarcodePattern(imageData);
              if (detected) {
                scanStatus.textContent = '🔍 Code-barres détecté! Analysing...';
              }
            }
          } catch (error) {
            console.error('Scanning error:', error);
          }
        }, 500); // Scan every 500ms for better performance
      };

      // Simple barcode pattern detection
      const detectBarcodePattern = (imageData) => {
        // This is a very basic implementation
        // In a real app, you'd use a proper barcode library like QuaggaJS
        const data = imageData.data;
        let blackPixels = 0;
        let whitePixels = 0;
        
        // Sample pixels to detect bar patterns
        for (let i = 0; i < data.length; i += 16) { // Sample every 4th pixel
          const avg = (data[i] + data[i + 1] + data[i + 2]) / 3;
          if (avg < 128) blackPixels++;
          else whitePixels++;
        }
        
        // If we have a good mix of black and white, might be a barcode
        const ratio = blackPixels / (blackPixels + whitePixels);
        return ratio > 0.3 && ratio < 0.7;
      };

      // Function to handle barcode detection
      const onBarcodeDetected = (barcode) => {
        isScanning = false;
        clearInterval(scanInterval);
        
        // Update the variant with the scanned barcode
        const newVariants = [...generatedVariants];
        newVariants[index].barCode = barcode;
        setGeneratedVariants(newVariants);
        
        // Close modal and stop scanning
        stream.getTracks().forEach(track => track.stop());
        modal.remove();
        setBarcodeScanning(prev => ({ ...prev, [index]: false }));
        
        // Show success message
        console.log(`Code-barres scanné: ${barcode}`);
      };

      // Start scanning
      startScanning();

      // Manual capture button - let user manually enter what they see
      captureButton.addEventListener('click', () => {
        const userInput = prompt('Veuillez saisir le code-barres que vous voyez:');
        if (userInput && userInput.trim()) {
          onBarcodeDetected(userInput.trim());
        }
      });

      // Close button handler
      closeButton.addEventListener('click', () => {
        isScanning = false;
        clearInterval(scanInterval);
        stream.getTracks().forEach(track => track.stop());
        modal.remove();
        setBarcodeScanning(prev => ({ ...prev, [index]: false }));
      });

      // Manual entry button handler
      manualEntryButton.addEventListener('click', () => {
        isScanning = false;
        clearInterval(scanInterval);
        stream.getTracks().forEach(track => track.stop());
        modal.remove();
        setBarcodeScanning(prev => ({ ...prev, [index]: false }));
        openManualBarcodeModal(index, 'camera_fallback');
      });
      
    } catch (error) {
      console.error('Error accessing camera:', error);
      setBarcodeScanning(prev => ({ ...prev, [index]: false }));
      
      // Enhanced error handling
      let errorReason = 'camera_error';
      let errorMessage = 'Caméra non disponible.';
      
      if (!window.isSecureContext) {
        errorReason = 'insecure_context';
        errorMessage = 'Camera access requires HTTPS. Please access via https:// or use manual entry.';
      } else if (error.name === 'NotAllowedError') {
        errorReason = 'camera_permission';
        errorMessage = 'Permission caméra refusée.';
      }
      
      // Fallback to manual entry
      openManualBarcodeModal(index, errorReason);
    }
  };
  // Add this function after closeManualBarcodeModal
const showSuccessToast = (message) => {
  // Create toast element
  const toastHtml = `
    <div id="success-toast" class="fixed top-4 right-4 z-50 transform transition-all duration-300 translate-x-full">
      <div class="bg-gradient-to-r from-green-500 to-green-600 text-white px-6 py-4 rounded-lg shadow-lg flex items-center space-x-3 max-w-sm">
        <div class="flex-shrink-0">
          <svg class="h-6 w-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"></path>
          </svg>
        </div>
        <div class="flex-1">
          <p class="text-sm font-medium">${message}</p>
        </div>
        <button onclick="document.getElementById('success-toast').remove()" class="flex-shrink-0 text-white hover:text-gray-200">
          <svg class="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
          </svg>
        </button>
      </div>
    </div>
  `;
  
  document.body.insertAdjacentHTML('beforeend', toastHtml);
  const toast = document.getElementById('success-toast');
  
  // Animate in
  setTimeout(() => {
    toast.classList.remove('translate-x-full');
  }, 100);
  
  // Auto remove after 3 seconds
  setTimeout(() => {
    if (toast) {
      toast.classList.add('translate-x-full');
      setTimeout(() => toast.remove(), 300);
    }
  }, 3000);
};
  return (
    <div className="sm:col-span-6 bg-white border border-gray-200 rounded-lg shadow-md overflow-hidden mt-6 w-full">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between px-4 sm:px-6 py-4 bg-gray-50 border-b border-gray-200">
        <div className="mb-2 sm:mb-0">
          <h4 className="text-base font-medium text-gray-900">Variantes générées</h4>
          <p className="mt-1 text-sm text-gray-500">Configurez les détails pour chaque variante</p>
        </div>
        <span className="inline-flex items-center px-3 py-0.5 rounded-full text-sm font-medium bg-[#174540] bg-opacity-10 text-[#174540]">
          {generatedVariants.length} variantes
        </span>
      </div>

      {/* Desktop View - Hidden on Mobile */}
      <div className="hidden md:block w-full" style={{ maxHeight: '430px', overflowY: 'auto', overflowX: 'hidden' }}>
        <table className="w-full divide-y divide-gray-200 table-fixed">
          <thead className="bg-gray-50 sticky top-0 z-10">
            <tr>
              <th scope="col" className="px-4 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-[5%]">
                <input
                  type="checkbox"
                  className="h-4 w-4 text-[#174540] focus:ring-[#174540] border-gray-300 rounded"
                  onChange={handleSelectAll}
                />
              </th>
              <th scope="col" className="px-4 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-[30%]">
                Variante
              </th>
              <th scope="col" className="px-4 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-[20%]">
                Référence
              </th>
              <th scope="col" className="px-4 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-[20%]">
                Code-barres
              </th>
              <th scope="col" className="px-4 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-[20%]">
                Image
              </th>
              <th scope="col" className="px-4 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-[15%]">
                Stock
              </th>
              <th scope="col" className="relative px-4 sm:px-6 py-3 w-[10%]">
                <span className="sr-only">Actions</span>
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {generatedVariants.map((variant, index) => (
              <tr key={variant.id} className="hover:bg-gray-50">
                <td className="px-4 sm:px-6 py-4 whitespace-nowrap">
                  <input
                    type="checkbox"
                    className="h-4 w-4 text-[#174540] focus:ring-[#174540] border-gray-300 rounded"
                    checked={variant.selected || false}
                    onChange={(e) => handleVariantSelection(index, e.target.checked)}
                  />
                </td>
                <td className="px-4 sm:px-6 py-4">
                  <div className="text-sm font-medium text-gray-900">
                    {variant.combination}
                  </div>
                </td>

                {/* SKU Input */}
                <td className="px-4 sm:px-6 py-4 whitespace-nowrap">
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <svg className="h-4 w-4 text-gray-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M4 2a2 2 0 00-2 2v12a2 2 0 002 2h12a2 2 0 002-2V4a2 2 0 00-2-2H4zm0 2h12v12H4V4zm2 3a1 1 0 011-1h5a1 1 0 110 2H7a1 1 0 01-1-1zm1 3a1 1 0 100 2h5a1 1 0 100-2H7z" clipRule="evenodd" />
                      </svg>
                    </div>
                    <input
                      type="text"
                      className="block w-full pl-10 pr-3 py-2 focus:ring-[#174540] focus:border-[#174540] sm:text-sm border border-gray-300 rounded-md bg-gray-50 shadow-sm"
                      placeholder="SKU"
                      value={variant.sku || ''}
                      onChange={(e) => handleSkuChange(index, e.target.value)}
                    />
                  </div>
                </td>

                {/* Barcode Input */}
                <td className="px-4 sm:px-6 py-4 whitespace-nowrap">
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <svg className="h-4 w-4 text-gray-400" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v1m6 11h2m-6 0h-2v4m0-11v3m0 0h.01M12 12h4.01M16 20h4M4 12h4m12 0h.01M5 8h2a1 1 0 001-1V5a1 1 0 00-1-1H5a1 1 0 00-1 1v2a1 1 0 001 1zm12 0h2a1 1 0 001-1V5a1 1 0 00-1-1h-2a1 1 0 00-1 1v2a1 1 0 001 1zM5 20h2a1 1 0 001-1v-2a1 1 0 00-1-1H5a1 1 0 00-1 1v2a1 1 0 001 1z" />
                      </svg>
                    </div>
                    <input
                      type="text"
                      className="block w-full pl-10 pr-20 py-2 focus:ring-[#174540] focus:border-[#174540] sm:text-sm border border-gray-300 rounded-md shadow-sm bg-gray-50"
                      placeholder="Code-barres"
                      value={variant.barCode || ''}
                      onChange={(e) => handleBarcodeInputChange(e, index)}
                      onFocus={(e) => handleBarcodeFocus(index, e.target.value)}
                      onBlur={(e) => handleBarcodeBlur(variant, index, e.target.value)}
                    />
                    <div className="absolute inset-y-0 right-0 pr-3 flex items-center space-x-1">
                      <button
                        type="button"
                        onClick={() => startBarcodeScanning(index)}
                        className="text-gray-400 hover:text-[#174540] focus:outline-none focus:text-[#174540] transition-colors"
                        title="Scanner avec caméra"
                        disabled={barcodeScanning[index]}
                      >
                        <svg className="h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
                        </svg>
                      </button>
                      <button
                        type="button"
                        onClick={() => openManualBarcodeModal(index)}
                        className="text-gray-400 hover:text-[#174540] focus:outline-none focus:text-[#174540] transition-colors"
                        title="Saisie manuelle"
                      >
                        <svg className="h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                        </svg>
                      </button>
                      <button
                        type="button"
                        onClick={() => generateUniqueBarcode(index)}
                        className="text-emerald-600 hover:text-emerald-800 focus:outline-none transition-colors"
                        title="Générer un code-barres unique"
                      >
                        <svg className="h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                        </svg>
                      </button>
                    </div>
                  </div>
                </td>

                {/* Image Upload */}
                <td className="px-4 sm:px-6 py-4 whitespace-nowrap">
                  <div className="flex flex-col items-center">
                    <div className="flex flex-wrap gap-1 mb-1">
                      {(variant.imageUrls || []).map((imgUrl, imgIdx) => (
                        <div key={`var-img-${index}-${imgIdx}`} className="relative h-10 w-10">
                          <img
                            src={imgUrl}
                            alt={`${variant.combination} preview ${imgIdx + 1}`}
                            className="h-10 w-10 object-cover rounded-md"
                          />
                          <button
                            type="button"
                            onClick={() => handleRemoveImage(index, imgIdx)}
                            className="absolute -top-1 -right-1 h-4 w-4 rounded-full bg-red-600 text-white flex items-center justify-center hover:bg-red-700 text-xs"
                          >
                            ×
                          </button>
                        </div>
                      ))}

                      {(!variant.imageUrls || variant.imageUrls.length < 3) && (
                        <button
                          type="button"
                          onClick={() => triggerFileInput(index)}
                          className="flex items-center justify-center h-10 w-10 border border-dashed border-gray-300 rounded-md text-gray-400 hover:text-gray-500 hover:border-gray-400 cursor-pointer transition-colors"
                        >
                          <svg className="h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                          </svg>
                          <input
                            ref={el => fileInputRefs.current[index] = el}
                            id={`variant-image-${index}`}
                            type="file"
                            accept="image/*"
                            className="hidden"
                            onChange={(e) => {
                              if (e.target.files && e.target.files[0]) {
                                handleAddImage(index, e.target.files[0]);
                                e.target.value = ''; // Reset input
                              }
                            }}
                          />
                        </button>
                      )}
                    </div>
                    {(variant.imageUrls?.length || 0) > 0 ? (
                      <span className="text-xs text-gray-500">{variant.imageUrls.length}/3</span>
                    ) : (
                      <span className="text-xs text-gray-500">Ajouter</span>
                    )}
                  </div>
                </td>

                {/* Stock Input */}
                <td className="px-4 sm:px-6 py-4 whitespace-nowrap">
                  <div className="flex items-center">
                    <div className="relative inline-flex rounded-md shadow-sm">
                      <button
                        type="button"
                        className="relative inline-flex items-center px-2 py-1 rounded-l-md border border-r-0 border-gray-300 bg-gray-50 text-gray-500 hover:bg-gray-100"
                        onClick={() => handleStockIncrement(index, -1)}
                      >
                        <svg className="h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 12H4" />
                        </svg>
                      </button>
                      <input
                        type="number"
                        className="block w-16 sm:w-20 text-center focus:ring-[#174540] focus:border-[#174540] sm:text-sm border-t border-b border-gray-300"
                        placeholder="0"
                        min="0"
                        value={variant.stock || '0'}
                        onChange={(e) => handleStockChange(index, e.target.value)}
                      />
                      <button
                        type="button"
                        className="relative inline-flex items-center px-2 py-1 rounded-r-md border border-l-0 border-gray-300 bg-gray-50 text-gray-500 hover:bg-gray-100"
                        onClick={() => handleStockIncrement(index, 1)}
                      >
                        <svg className="h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v12m6-6H6" />
                        </svg>
                      </button>
                    </div>
                  </div>
                </td>

                {/* Delete Action */}
                <td className="px-4 sm:px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                  <button
                    type="button"
                    className="text-red-600 hover:text-red-900 transition-colors"
                    onClick={() => handleDeleteVariant(index)}
                  >
                    <svg className="h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                    </svg>
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Mobile View - Hidden on Desktop */}
      <div className="md:hidden" style={{ maxHeight: '430px', overflowY: 'auto', overflowX: 'hidden' }}>
        <div className="px-4 py-2 bg-gray-50 border-b border-gray-200 flex items-center">
          <input
            type="checkbox"
            className="h-4 w-4 text-[#174540] focus:ring-[#174540] border-gray-300 rounded mr-2"
            onChange={handleSelectAll}
          />
          <span className="text-xs font-medium text-gray-500 uppercase">Sélectionner tout</span>
        </div>

        <ul className="divide-y divide-gray-200">
          {generatedVariants.map((variant, index) => (
            <li key={variant.id} className="bg-white">
              <div className="px-4 py-3 flex items-start">
                <input
                  type="checkbox"
                  className="h-4 w-4 mt-1 text-[#174540] focus:ring-[#174540] border-gray-300 rounded"
                  checked={variant.selected || false}
                  onChange={(e) => handleVariantSelection(index, e.target.checked)}
                />
                <div className="ml-3 flex-1">
                  <div className="flex items-center justify-between">
                    <div className="text-sm font-medium text-gray-900 flex-1 mr-2">{variant.combination}</div>
                    <div className="flex items-center">
                      <button
                        type="button"
                        className="p-1 text-red-600 hover:text-red-900 transition-colors mr-2"
                        onClick={() => handleDeleteVariant(index)}
                      >
                        <svg className="h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                        </svg>
                      </button>
                      <button
                        type="button"
                        className="p-1 text-gray-500 hover:text-gray-700"
                        onClick={() => toggleRow(index)}
                      >
                        {expandedRow === index ? (
                          <svg className="h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                            <path fillRule="evenodd" d="M14.707 12.707a1 1 0 01-1.414 0L10 9.414l-3.293 3.293a1 1 0 01-1.414-1.414l4-4a1 1 0 011.414 0l4 4a1 1 0 010 1.414z" clipRule="evenodd" />
                          </svg>
                        ) : (
                          <svg className="h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                            <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
                          </svg>
                        )}
                      </button>
                    </div>
                  </div>

                  {/* Preview information without expanding */}
                  <div className="mt-1 flex items-center justify-between">
                    <div className="flex items-center space-x-2">
                      <span className="text-xs text-gray-500">Stock:</span>
                      <span className="text-xs font-medium">{variant.stock || '0'}</span>
                    </div>

                    <div className="flex items-center">
                      {(variant.imageUrls && variant.imageUrls.length > 0) ? (
                        <div className="flex -space-x-1">
                          {variant.imageUrls.slice(0, 2).map((imgUrl, imgIdx) => (
                            <img
                              key={`preview-${index}-${imgIdx}`}
                              src={imgUrl}
                              alt=""
                              className="h-6 w-6 rounded-full ring-2 ring-white object-cover"
                            />
                          ))}
                          {variant.imageUrls.length > 2 && (
                            <div className="flex items-center justify-center h-6 w-6 rounded-full bg-gray-100 ring-2 ring-white text-xs text-gray-500">
                              +{variant.imageUrls.length - 2}
                            </div>
                          )}
                        </div>
                      ) : (
                        <span className="text-xs text-gray-500">Aucune image</span>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* Expanded detailed view */}
              {expandedRow === index && (
                <div className="px-4 pb-4 pt-2 ml-7 border-t border-gray-100">
                  <div className="space-y-4">
                    {/* SKU field */}
                    <div>
                      <label htmlFor={`mobile-sku-${index}`} className="block text-xs font-medium text-gray-700 mb-1">
                        Référence (SKU)
                      </label>
                      <div className="relative">
                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                          <svg className="h-4 w-4 text-gray-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                            <path fillRule="evenodd" d="M4 2a2 2 0 00-2 2v12a2 2 0 002 2h12a2 2 0 002-2V4a2 2 0 00-2-2H4zm0 2h12v12H4V4zm2 3a1 1 0 011-1h5a1 1 0 110 2H7a1 1 0 01-1-1zm1 3a1 1 0 100 2h5a1 1 0 100-2H7z" clipRule="evenodd" />
                          </svg>
                        </div>
                        <input
                          id={`mobile-sku-${index}`}
                          type="text"
                          className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md shadow-sm text-sm bg-gray-50 focus:ring-[#174540] focus:border-[#174540]"
                          placeholder="SKU"
                          value={variant.sku || ''}
                          onChange={(e) => handleSkuChange(index, e.target.value)}
                        />
                      </div>
                    </div>

                    {/* Barcode field */}
                    <div>
                      <label htmlFor={`mobile-barcode-${index}`} className="block text-xs font-medium text-gray-700 mb-1">
                        Code-barres
                      </label>
                      <div className="relative">
                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                          <svg className="h-4 w-4 text-gray-400" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v1m6 11h2m-6 0h-2v4m0-11v3m0 0h.01M12 12h4.01M16 20h4M4 12h4m12 0h.01M5 8h2a1 1 0 001-1V5a1 1 0 00-1-1H5a1 1 0 00-1 1v2a1 1 0 001 1zm12 0h2a1 1 0 001-1V5a1 1 0 00-1-1h-2a1 1 0 00-1 1v2a1 1 0 001 1zM5 20h2a1 1 0 001-1v-2a1 1 0 00-1-1H5a1 1 0 00-1 1v2a1 1 0 001 1z" />
                          </svg>
                        </div>
                        <input
                          id={`mobile-barcode-${index}`}
                          type="text"
                          className="block w-full pl-10 pr-10 py-2 border border-gray-300 rounded-md shadow-sm text-sm bg-gray-50 focus:ring-[#174540] focus:border-[#174540]"
                          placeholder="Code-barres"
                          value={variant.barCode || ''}
                          onChange={(e) => handleBarcodeInputChange(e, index)}
                          onFocus={(e) => handleBarcodeFocus(index, e.target.value)}
                          onBlur={(e) => handleBarcodeBlur(variant, index, e.target.value)}
                        />
                        <div className="absolute inset-y-0 right-0 pr-3 flex items-center space-x-1">
                          <button
                            type="button"
                            onClick={() => openManualBarcodeModal(index)}
                            className="text-gray-400 hover:text-[#174540] focus:outline-none focus:text-[#174540] transition-colors"
                            title="Saisie manuelle"
                          >
                            <svg className="h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                            </svg>
                          </button>
                          <button
                            type="button"
                            onClick={() => generateUniqueBarcode(index)}
                            className="text-emerald-600 hover:text-emerald-800 focus:outline-none transition-colors"
                            title="Générer un code-barres unique"
                          >
                            <svg className="h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                            </svg>
                          </button>
                        </div>
                      </div>
                    </div>

                    {/* Stock field */}
                    <div>
                      <label htmlFor={`mobile-stock-${index}`} className="block text-xs font-medium text-gray-700 mb-1">
                        Stock
                      </label>
                      <div className="flex items-center">
                        <div className="relative inline-flex rounded-md shadow-sm">
                          <button
                            type="button"
                            className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-r-0 border-gray-300 bg-gray-50 text-gray-500 hover:bg-gray-100"
                            onClick={() => handleStockIncrement(index, -1)}
                          >
                            <svg className="h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 12H4" />
                            </svg>
                          </button>
                          <input
                            id={`mobile-stock-${index}`}
                            type="number"
                            className="block w-full sm:w-24 text-center text-sm focus:ring-[#174540] focus:border-[#174540] border-t border-b border-gray-300"
                            placeholder="0"
                            min="0"
                            value={variant.stock || '0'}
                            onChange={(e) => handleStockChange(index, e.target.value)}
                          />
                          <button
                            type="button"
                            className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-l-0 border-gray-300 bg-gray-50 text-gray-500 hover:bg-gray-100"
                            onClick={() => handleStockIncrement(index, 1)}
                          >
                            <svg className="h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v12m6-6H6" />
                            </svg>
                          </button>
                        </div>
                      </div>
                    </div>

                    {/* Images field */}
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">
                        Images ({(variant.imageUrls?.length || 0)}/3)
                      </label>
                      <div className="flex flex-wrap gap-2">
                        {(variant.imageUrls || []).map((imgUrl, imgIdx) => (
                          <div key={`mobile-img-${index}-${imgIdx}`} className="relative h-16 w-16">
                            <img
                              src={imgUrl}
                              alt={`${variant.combination} preview ${imgIdx + 1}`}
                              className="h-16 w-16 object-cover rounded-md border border-gray-200"
                            />
                            <button
                              type="button"
                              onClick={() => handleRemoveImage(index, imgIdx)}
                              className="absolute -top-1 -right-1 h-5 w-5 rounded-full bg-red-600 text-white flex items-center justify-center hover:bg-red-700 text-xs"
                            >
                              ×
                            </button>
                          </div>
                        ))}

                        {(!variant.imageUrls || variant.imageUrls.length < 3) && (
                          <button
                            type="button"
                            onClick={() => triggerFileInput(`mobile-${index}`)}
                            className="flex items-center justify-center h-16 w-16 border border-dashed border-gray-300 rounded-md text-gray-400 hover:text-gray-500 hover:border-gray-400 cursor-pointer transition-colors"
                          >
                            <svg className="h-6 w-6" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                            </svg>
                            <input
                              ref={el => fileInputRefs.current[`mobile-${index}`] = el}
                              id={`mobile-variant-image-${index}`}
                              type="file"
                              accept="image/*"
                              className="hidden"
                              onChange={(e) => {
                                if (e.target.files && e.target.files[0]) {
                                  handleAddImage(index, e.target.files[0]);
                                  e.target.value = ''; // Reset input
                                }
                              }}
                            />
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </li>
          ))}
        </ul>
      </div>
      {manualBarcodeModal.isOpen && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 bg-gray-900 bg-opacity-50 transition-opacity" onClick={closeManualBarcodeModal}></div>

            <div className="inline-block align-bottom bg-white rounded-xl text-left overflow-hidden shadow-2xl transform transition-all sm:my-8 sm:align-middle sm:max-w-md sm:w-full">
              <div className="bg-white px-6 pt-6 pb-4">
                <div className="flex items-start">
                  <div className="flex-shrink-0">
                    <div className="flex items-center justify-center h-12 w-12 rounded-full bg-blue-100">
                      <svg className="h-6 w-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v1m6 11h2m-6 0h-2v4m0-11v3m0 0h.01M12 12h4.01M16 20h4M4 12h4m12 0h.01M5 8h2a1 1 0 001-1V5a1 1 0 00-1-1H5a1 1 0 00-1 1v2a1 1 0 001 1zm12 0h2a1 1 0 001-1V5a1 1 0 00-1-1h-2a1 1 0 00-1 1v2a1 1 0 001 1zM5 20h2a1 1 0 001-1v-2a1 1 0 00-1-1H5a1 1 0 00-1 1v2a1 1 0 001 1z" />
                      </svg>
                    </div>
                  </div>
                  <div className="ml-4 w-full">
                    <h3 className="text-lg leading-6 font-semibold text-gray-900 mb-2">
                      Saisir le code-barres
                    </h3>
                    <p className="text-sm text-gray-500 mb-4">
                      Saisissez le code-barres pour cette variante.
                    </p>

                    <div className="relative">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <svg className="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                      </div>
                      <input
                        type="text"
                        className={`block w-full pl-10 pr-3 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 sm:text-sm ${manualBarcodeModal.error ? 'border-red-300 bg-red-50' : 'border-gray-300'
                          }`}
                        placeholder="Entrez le code-barres..."
                        value={manualBarcodeModal.inputValue}
                        onChange={(e) => setManualBarcodeModal(prev => ({
                          ...prev,
                          inputValue: e.target.value,
                          error: ''
                        }))}
                        onKeyPress={(e) => {
                          if (e.key === 'Enter') {
                            handleManualBarcodeSubmit();
                          }
                        }}
                        autoFocus
                      />
                    </div>

                    {manualBarcodeModal.error && (
                      <div className="mt-2 flex items-center">
                        <svg className="h-4 w-4 text-red-500 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        <p className="text-sm text-red-600">{manualBarcodeModal.error}</p>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              <div className="bg-gray-50 px-6 py-4 flex flex-col sm:flex-row sm:justify-end space-y-2 sm:space-y-0 sm:space-x-3">
                <button
                  type="button"
                  onClick={closeManualBarcodeModal}
                  className="w-full sm:w-auto inline-flex justify-center rounded-lg border border-gray-300 shadow-sm px-4 py-2 bg-white text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                >
                  Annuler
                </button>
                <button
                  type="button"
                  onClick={handleManualBarcodeSubmit}
                  className="w-full sm:w-auto inline-flex justify-center rounded-lg border border-transparent shadow-sm px-4 py-2 bg-blue-600 text-sm font-medium text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                >
                  Confirmer
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default VariantsTable;